import { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Filter } from 'lucide-react';
import ModuleContainer from '../layout/ModuleContainer.jsx';

const TYPE_COLORS = {
  CONVERGENCE:   'bg-korinth-accent/10 text-korinth-accent',
  DIVERGENCE:    'bg-purple-500/10 text-purple-400',
  FUTURES_ICT:   'bg-blue-500/10 text-blue-400',
  CONGRESSIONAL: 'bg-amber-500/10 text-amber-400',
  ON_CHAIN:      'bg-cyan-500/10 text-cyan-400',
  SMART_MONEY:   'bg-indigo-500/10 text-indigo-400',
  CAPITULATION:  'bg-red-500/10 text-red-400',
  FEAR_GREED:    'bg-orange-500/10 text-orange-400',
  MOMENTUM:      'bg-teal-500/10 text-teal-400',
  NEWS_SENTIMENT:'bg-violet-500/10 text-violet-400',
  REGIME:        'bg-emerald-500/10 text-emerald-400',
};

const CONVICTION_COLORS = {
  HIGH:   'bg-korinth-accent/20 text-korinth-accent',
  MEDIUM: 'bg-korinth-warning/20 text-korinth-warning',
  LOW:    'bg-korinth-muted/20 text-korinth-muted',
};

function formatTimestamp(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleString('en-US', { timeZone: 'Asia/Dubai', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
}

export default function SignalFeed() {
  const [signals, setSignals] = useState([]);
  const [filter, setFilter] = useState({ type: 'all', conviction: 'all' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchFeed() {
      try {
        const params = new URLSearchParams({ limit: '50' });
        if (filter.type !== 'all') params.set('type', filter.type);
        if (filter.conviction !== 'all') params.set('conviction', filter.conviction);
        const res = await fetch(`/api/signals/feed?${params}`);
        if (res.ok) setSignals(await res.json());
      } catch { /* silent */ }
      finally { setLoading(false); }
    }
    fetchFeed();
    const interval = setInterval(fetchFeed, 30000);
    return () => clearInterval(interval);
  }, [filter]);

  return (
    <ModuleContainer title="Signal Feed" subtitle="All signals from all sources — real-time">
      {/* Filters */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Filter className="w-3 h-3 text-korinth-muted" />
        <select
          value={filter.type}
          onChange={e => setFilter(f => ({ ...f, type: e.target.value }))}
          className="bg-korinth-bg border border-korinth-border rounded px-2 py-1 text-[10px] font-mono text-korinth-text"
        >
          <option value="all">All Types</option>
          <option value="CONVERGENCE">Convergence</option>
          <option value="DIVERGENCE">Divergence</option>
          <option value="FUTURES_ICT">Futures ICT</option>
          <option value="CONGRESSIONAL">Congressional</option>
          <option value="ON_CHAIN">On-Chain</option>
          <option value="CAPITULATION">Capitulation</option>
          <option value="FEAR_GREED">Fear/Greed</option>
          <option value="SMART_MONEY">Smart Money</option>
        </select>
        <select
          value={filter.conviction}
          onChange={e => setFilter(f => ({ ...f, conviction: e.target.value }))}
          className="bg-korinth-bg border border-korinth-border rounded px-2 py-1 text-[10px] font-mono text-korinth-text"
        >
          <option value="all">All Conviction</option>
          <option value="HIGH">HIGH</option>
          <option value="MEDIUM">MEDIUM</option>
          <option value="LOW">LOW</option>
        </select>
      </div>

      {/* Signal list */}
      <div className="space-y-1.5 max-h-96 overflow-y-auto">
        {signals.length > 0 ? signals.map((s, i) => (
          <div key={s.id || i} className="flex items-center gap-2 px-3 py-2 bg-korinth-bg rounded border border-korinth-border/30">
            {s.direction === 'bullish' ? (
              <TrendingUp className="w-4 h-4 text-korinth-accent flex-shrink-0" />
            ) : (
              <TrendingDown className="w-4 h-4 text-korinth-danger flex-shrink-0" />
            )}
            <span className="text-xs font-mono font-bold text-white w-12">{s.asset}</span>
            <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded ${TYPE_COLORS[s.signal_type] || 'bg-korinth-border text-korinth-muted'}`}>
              {s.signal_type}
            </span>
            <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded ${CONVICTION_COLORS[s.conviction] || ''}`}>
              {s.conviction}
            </span>
            <span className="text-[10px] font-mono text-korinth-muted ml-auto">{s.score}/10</span>
            <span className="text-[10px] font-mono text-korinth-muted/60">{formatTimestamp(s.created_at)}</span>
          </div>
        )) : (
          <p className="text-xs text-korinth-muted font-mono text-center py-4">
            {loading ? 'Loading signals...' : 'No signals match filters'}
          </p>
        )}
      </div>
    </ModuleContainer>
  );
}
